import React, {useEffect, useState} from 'react';
import DashboardHeader from "../../components/DashboardHeader";
import {useUserContext} from "../../contexts/user_context";
import {useNavigationContext} from "../../contexts/NavContext/nav-context";
import {AppView} from "../../App";
import {withApiErrorHandler} from "../../error_handling";
import {getLlmUsage, getUploadPageUsage, TIER_LABELS, UserLlmUsage, UserUploadPageUsage} from "../../api/users";

const fmt = (n: number) => n.toLocaleString();

/** "1,234 / 5,000" or "1,234 / Unlimited". */
const usageText = (used: number, limit: number | null) =>
    `${fmt(used)} / ${limit === null ? "Unlimited" : fmt(limit)}`;

/**
 * Minimal account settings page. Shows basic profile info and lets the user
 * permanently delete their account (and the workspaces they own).
 */
const UserSettings: React.FC = () => {
    const {currentUser, deleteAccount} = useUserContext()!;
    const {navigateTo} = useNavigationContext()!;

    const [confirming, setConfirming] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [pageUsage, setPageUsage] = useState<UserUploadPageUsage | null>(null);
    const [llmUsage, setLlmUsage] = useState<UserLlmUsage | null>(null);

    // Quotas are enforced server-side; this just shows the user where they stand.
    useEffect(() => {
        const load = async () => {
            const [pages, llm] = await Promise.all([
                withApiErrorHandler(() => getUploadPageUsage(), {titleQualifier: "Error fetching upload usage", navigateTo}),
                withApiErrorHandler(() => getLlmUsage(), {titleQualifier: "Error fetching AI usage", navigateTo}),
            ]);
            if (pages) setPageUsage(pages);
            if (llm) setLlmUsage(llm);
        };
        void load();
    }, [navigateTo]);

    const tierLabel = currentUser?.tier ? TIER_LABELS[currentUser.tier] ?? currentUser.tier : undefined;
    const isUnlimitedUploads = pageUsage != null
        && pageUsage.daily_limit === null && pageUsage.lifetime_limit === null;

    // Require the user to type their exact email to arm the delete button — a
    // deliberate, hard-to-do-by-accident gate for an irreversible action.
    const confirmMatches =
        !!currentUser?.email &&
        confirmText.trim().toLowerCase() === currentUser.email.toLowerCase();

    const resetConfirm = () => {
        setConfirming(false);
        setConfirmText("");
    };

    const handleDelete = async () => {
        if (!confirmMatches) return;
        setDeleting(true);
        const ok = await withApiErrorHandler(
            () => deleteAccount(),
            {titleQualifier: "Delete Account"}
        );
        setDeleting(false);
        // deleteAccount returns void; treat a non-thrown result as success.
        if (ok !== null) {
            navigateTo({view: AppView.Login});
        } else {
            resetConfirm();
        }
    };

    const InfoRow: React.FC<{ label: string; value?: React.ReactNode }> = ({label, value}) => (
        <div className="flex justify-between gap-4 py-2.5 border-b border-zinc-100 last:border-0">
            <span className="text-sm text-zinc-500">{label}</span>
            <span className="text-sm font-medium text-zinc-800 text-right break-all">{value || "—"}</span>
        </div>
    );

    return (
        <DashboardHeader>
            <div className="flex flex-1 flex-col h-full overflow-auto">
                <div className="flex flex-row items-center p-4">
                    <button
                        onClick={() => navigateTo({view: AppView.MyDashboard})}
                        className="mr-3 text-sm text-zinc-500 hover:text-zinc-800"
                    >
                        &larr; Back
                    </button>
                    <span className="text-2xl font-semibold text-[#15151b]">Account Settings</span>
                </div>

                <div className="px-4 pb-10 max-w-2xl w-full">
                    {/* Profile info */}
                    <section className="rounded-xl border border-zinc-200 bg-white p-5 mb-6">
                        <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-400 mb-3">
                            Profile
                        </h2>
                        <InfoRow
                            label="Name"
                            value={[currentUser?.first_name, currentUser?.surname].filter(Boolean).join(" ")}
                        />
                        <InfoRow label="Email" value={currentUser?.email}/>
                        <InfoRow label="Plan" value={tierLabel}/>
                    </section>

                    {/* Usage & limits */}
                    <section className="rounded-xl border border-zinc-200 bg-white p-5 mb-6">
                        <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-400 mb-1">
                            Usage &amp; Limits
                        </h2>
                        <p className="text-xs text-zinc-500 mb-3">
                            Daily limits reset at midnight UTC. PDFs count by page; text and email
                            files count roughly one page per 3,000 characters.
                        </p>
                        <InfoRow
                            label="Pages uploaded today"
                            value={pageUsage ? usageText(pageUsage.pages_today, pageUsage.daily_limit) : "…"}
                        />
                        <InfoRow
                            label="Pages uploaded all-time"
                            value={pageUsage ? usageText(pageUsage.pages_lifetime, pageUsage.lifetime_limit) : "…"}
                        />
                        <InfoRow
                            label="AI tokens used today"
                            value={llmUsage ? usageText(llmUsage.tokens_used_today, llmUsage.tier_limit) : "…"}
                        />
                        {(pageUsage?.is_over_limit || llmUsage?.is_over_limit) && (
                            <p className="mt-3 text-xs text-amber-700">
                                You&apos;ve hit a limit for today. Uploads or AI features will resume after the reset.
                            </p>
                        )}
                        {isUnlimitedUploads && (
                            <p className="mt-3 text-xs text-zinc-500">
                                Your plan has no upload limit. The AI token cap is a safety net, not a plan limit.
                            </p>
                        )}
                    </section>

                    {/* Danger zone */}
                    <section className="rounded-xl border border-red-200 bg-red-50 p-5">
                        <h2 className="text-sm font-semibold uppercase tracking-wider text-red-500 mb-2">
                            Danger Zone
                        </h2>
                        <p className="text-sm text-red-900/80 mb-4">
                            Permanently delete your account. This removes your profile and every
                            workspace you own, including all uploaded documents. This action
                            cannot be undone.
                        </p>

                        {!confirming ? (
                            <button
                                onClick={() => setConfirming(true)}
                                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                            >
                                Delete my account
                            </button>
                        ) : (
                            <div className="flex flex-col gap-3">
                                <p className="text-sm font-medium text-red-900">
                                    This permanently deletes your account and all your workspaces.
                                    To confirm, type your email
                                    {" "}
                                    <span className="font-semibold">{currentUser?.email}</span>
                                    {" "}below.
                                </p>
                                <input
                                    type="email"
                                    autoComplete="off"
                                    value={confirmText}
                                    onChange={(e) => setConfirmText(e.target.value)}
                                    placeholder={currentUser?.email}
                                    disabled={deleting}
                                    className="w-full max-w-sm rounded-lg border border-red-300 bg-white px-3 py-2 text-sm text-zinc-800 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 disabled:opacity-60"
                                />
                                <div className="flex gap-3">
                                    <button
                                        onClick={handleDelete}
                                        disabled={deleting || !confirmMatches}
                                        className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        {deleting ? "Deleting…" : "Permanently delete account"}
                                    </button>
                                    <button
                                        onClick={resetConfirm}
                                        disabled={deleting}
                                        className="rounded-lg border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-60"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </DashboardHeader>
    );
};

export default UserSettings;
